import type { IconName } from '../../components/Icon';
import { Feature, hasFeature } from '../../utils/features';
import { can, StaffCommunity } from '../../utils/permissions';

export type SidebarItem =
  | {
      type: 'link';
      label: string;
      route: string;
      icon: IconName;
      featureFlag?: string;
      feature?: Feature;
      // Visible con alguno de estos permisos en la comunidad activa.
      permission?: string[];
      // O con un rol cuyo nombre empieza con este prefijo (ej. 'vinte').
      requiredRolePrefix?: string;
    }
  | {
      type: 'group';
      label: string;
      behavior: 'expand-only' | 'landing';
      landingRoute?: string;
      icon: IconName;
      items: {
        label: string;
        route: string;
        featureFlag?: string;
        feature?: Feature;
        permission?: string[];
        // Solo en comunidades con Sentinel (bitácora nueva) encendido
        sentinel?: boolean;
      }[];
      featureFlag?: string;
      feature?: Feature;
      permission?: string[];
      requiredRolePrefix?: string;
    };

export const sidebarStructure: SidebarItem[] = [
  {
    type: 'link',
    label: 'Resumen',
    route: 'Admin.Home',
    icon: 'navResumen',
    permission: ['dashboard.view'],
  },
  {
    type: 'link',
    label: 'Comunicados',
    route: 'Admin.Announcements.Index',
    icon: 'navComunicados',
    permission: ['announcements.manage'],
  },
  {
    type: 'link',
    label: 'Viviendas',
    route: 'Admin.Units.Index',
    icon: 'navViviendas',
    permission: ['units.view'],
  },
  {
    type: 'link',
    label: 'Finanzas',
    route: 'Admin.Fees.Index',
    icon: 'navFinanzas',
    permission: ['fees.view'],
  },
  {
    type: 'group',
    label: 'Vinte',
    behavior: 'landing',
    landingRoute: 'Admin.Vinte.PaymentRequests',
    icon: 'vinte',
    feature: 'vinte',
    permission: ['vinte.payment_requests.view'],
    requiredRolePrefix: 'vinte',
    items: [
      { label: 'Solicitudes de pago', route: 'Admin.Vinte.PaymentRequests' },
      { label: 'Proveedores', route: 'Admin.Vinte.Providers' },
      { label: 'Viviendas', route: 'Admin.Vinte.Units' },
      { label: 'Reportes', route: 'Admin.Vinte.Reports' },
    ],
  },
  {
    type: 'group',
    label: 'Accesos',
    behavior: 'landing',
    landingRoute: 'Admin.AccessControl.Index',
    icon: 'navAccesos',
    permission: ['access.view'],
    items: [
      { label: 'Centro de control', route: 'Admin.Sentinel.ControlCenter', sentinel: true },
      { label: 'Eventos', route: 'Admin.AccessControl.EventsDashboard' },
      { label: 'Cámaras', route: 'Admin.AccessControl.Cameras', feature: 'argos', permission: ['argos.cameras.view'] },
      { label: 'Autorizaciones', route: 'Admin.AccessAuthorizations.Index', featureFlag: 'access_authorization.enabled', permission: ['access.view', 'access_authorizations.review'] },
    ],
  },
  {
    type: 'link',
    label: 'Reportes y solicitudes',
    route: 'Admin.Tickets.Index',
    icon: 'navIncidencias',
    featureFlag: 'tickets.enabled',
    permission: ['tickets.view', 'tickets.manage'],
  },
  // {
  //   type: 'link',
  //   label: 'Demo Fullscreen',
  //   route: 'Admin.Web.Demo',
  //   icon: 'navResumen',
  // },
  {
    type: 'link',
    label: 'Configuración',
    route: 'Admin.Settings.Index',
    icon: 'navAjustes',
    permission: ['settings.manage', 'staff.manage'],
  },
];


type VisibilityItem = { featureFlag?: string; feature?: Feature; permission?: string[]; requiredRolePrefix?: string; sentinel?: boolean };

/**
 * ¿Se muestra el item en la comunidad activa? Flag de settings (lo activa el
 * admin), módulo contratado (features) y permiso del usuario. Con
 * `requiredRolePrefix`, un rol con ese prefijo cuenta como permiso.
 */
export function isSidebarItemVisible(item: VisibilityItem, community?: StaffCommunity): boolean {
  if (!community) return false;

  if (item.featureFlag && !getPath(community.settings, item.featureFlag)) return false;
  if (item.feature && !hasFeature(community, item.feature)) return false;
  if (item.sentinel && (!community.sentinel_mode || community.sentinel_mode === 'off')) return false;

  if (!item.permission) return true;

  const byRole = !!item.requiredRolePrefix
    && community.roles?.some(role => role.name.startsWith(item.requiredRolePrefix!));

  return can(community, ...item.permission) || byRole;
}

/** Items visibles, con los subitems de cada grupo ya filtrados. */
export function visibleSidebarItems(community?: StaffCommunity): SidebarItem[] {
  return sidebarStructure
    .filter(item => isSidebarItemVisible(item, community))
    .map(item =>
      item.type === 'group'
        ? { ...item, items: item.items.filter(sub => isSidebarItemVisible(sub, community)) }
        : item
    );
}

/** Pantalla de entrada al panel: la primera sección visible. */
export function firstSidebarRoute(community?: StaffCommunity): string {
  const first = visibleSidebarItems(community)[0];
  if (!first) return 'Admin.Settings.Index';
  return first.type === 'group' ? (first.landingRoute ?? first.items[0]?.route) : first.route;
}

function getPath(obj: any, path: string): any {
  return path.split('.').reduce((acc, key) => (acc && typeof acc === 'object' ? acc[key] : undefined), obj);
}

/*
Asi se agregaría un grupo expandible (ejemplo Finanzas con Adeudos y Pagos):
{
    type: 'group',
    label: 'Finanzas',
    behavior: 'expand-only',
    icon: 'cobros',
    items: [
      { label: 'Adeudos', route: 'Admin.Fees.Index' },
      { label: 'Pagos', route: 'Admin.Payments.Index' },
    ],
  },
*/
